import { useEffect, useMemo, useState } from 'react';
import { api, type PiaItem } from '@/api/client';
import { mockPia, useMock } from '@/api/mock';

function riskBadge(score: number) {
  if (score >= 61) return 'badge-bad';
  if (score >= 31) return 'badge-warn';
  return 'badge-good';
}

export function PrivacyAssessmentsPage() {
  const [items, setItems] = useState<PiaItem[]>([]);

  useEffect(() => {
    if (useMock()) {
      setItems(mockPia);
      return;
    }
    api.piaList().then(setItems).catch(() => setItems(mockPia));
  }, []);

  const stats = useMemo(() => ({
    total: items.length,
    overdue: items.filter(i => i.status === 'Overdue').length,
    inReview: items.filter(i => i.status === 'InReview').length,
    avgRisk: items.length ? (items.reduce((sum, i) => sum + i.riskScore, 0) / items.length).toFixed(1) : '0.0',
  }), [items]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Privacy Assessments</h1>
          <p className="text-sm text-ink-muted mt-1">Full PIA list, risk scoring, and review progress.</p>
        </div>
        <button className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm hover:bg-accent/20">New PIA Assessment</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card"><div className="card-title">Total</div><div className="kpi-value">{stats.total}</div></div>
        <div className="card"><div className="card-title">In Review</div><div className="kpi-value text-warn">{stats.inReview}</div></div>
        <div className="card"><div className="card-title">Overdue</div><div className="kpi-value text-bad">{stats.overdue}</div></div>
        <div className="card"><div className="card-title">Avg Risk</div><div className="kpi-value">{stats.avgRisk}</div></div>
      </div>

      <div className="card overflow-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="text-ink-muted border-b border-border">
            <tr>
              <th className="text-left py-2">Model / Feature</th>
              <th className="text-left py-2">Status</th>
              <th className="text-left py-2">Risk Score</th>
              <th className="text-left py-2">Reviewer</th>
              <th className="text-left py-2">Reviewer Email</th>
              <th className="text-left py-2">Due Date</th>
              <th className="text-left py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-b border-border/50 last:border-0">
                <td className="py-2">{i.modelOrFeatureName}</td>
                <td className="py-2">{i.status}</td>
                <td className="py-2"><span className={riskBadge(i.riskScore)}>{i.riskScore}</span></td>
                <td className="py-2">{i.reviewer.reviewerName}</td>
                <td className="py-2">{i.reviewer.reviewerEmail}</td>
                <td className="py-2">{new Date(i.dueDate).toLocaleDateString()}</td>
                <td className="py-2"><button className="text-accent-cyan hover:underline">Open Questionnaire</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
